import {
  FieldworkAudience,
  FieldworkAudienceType,
  MemberSettingCode,
  Question
} from '../../data/model/audience'
import { getIsEnabledInSettings } from './utils'

/** Screener order. `position` is renumbered on every move; `number` is creation order and never moves. */
export const inScreenerOrder = (
  members: FieldworkAudience[] | undefined
): FieldworkAudience[] =>
  (members ?? []).slice().sort((a, b) => a.position - b.position)

/** Applied as a criterion, but never asked as a survey question. */
export const isHiddenFromSurveyQuestion = (
  member: FieldworkAudience
): boolean =>
  member.memberType === FieldworkAudienceType.Standard &&
  getIsEnabledInSettings(
    member.memberItem.settingValues,
    MemberSettingCode.HideSurveyQuestion
  )

/** The audience mutations key on the question id, not `FieldworkAudience.id`. */
export const getAudienceMemberQuestionId = (
  member: FieldworkAudience
): string =>
  member.memberType === FieldworkAudienceType.Standard
    ? member.memberItem.criterion.question.questionId
    : member.memberItem.questionLk

export const getAudienceMemberQuestion = (
  member: FieldworkAudience
): Question =>
  member.memberType === FieldworkAudienceType.Standard
    ? member.memberItem.criterion.question
    : member.memberItem.question

/** `A1..An` over the members respondents are asked; hidden ones take no number. */
export const getSurveyQuestionLabels = (
  members: FieldworkAudience[]
): (string | undefined)[] => {
  let asked = 0
  return members.map((member) =>
    isHiddenFromSurveyQuestion(member) ? undefined : `A${(asked += 1)}`
  )
}

/**
 * Maps a drop index in a surface's visible list to one in the full member list.
 * Both are indexes into the list with the moved member already removed, which is
 * what the backend splits on. Surfaces that show every member pass an `isHidden`
 * that is always false, making this the identity.
 */
export const mapVisibleIndexToFullPosition = (
  members: FieldworkAudience[],
  movedMemberId: string,
  visibleDestinationIndex: number,
  isHidden: (member: FieldworkAudience) => boolean = isHiddenFromSurveyQuestion
): number => {
  const fullRemaining = members.filter((m) => m.id !== movedMemberId)
  const visibleRemaining = fullRemaining.filter((m) => !isHidden(m))

  const anchor = visibleRemaining.find(
    (_, index) => index === visibleDestinationIndex
  )
  if (anchor) {
    return fullRemaining.findIndex((m) => m.id === anchor.id)
  }

  if (visibleRemaining.length === 0) {
    return fullRemaining.length
  }
  const lastVisible = visibleRemaining[visibleRemaining.length - 1]
  return fullRemaining.findIndex((m) => m.id === lastVisible.id) + 1
}

export const moveMemberInList = (
  members: FieldworkAudience[],
  movedMemberId: string,
  toIndex: number
): FieldworkAudience[] => {
  const moved = members.find((m) => m.id === movedMemberId)
  if (!moved) {
    return members
  }
  const remaining = members.filter((m) => m.id !== movedMemberId)
  return [...remaining.slice(0, toIndex), moved, ...remaining.slice(toIndex)]
}
